import { Injectable, NgZone } from '@angular/core';
import { FFmpeg } from '@ffmpeg/ffmpeg';
import { fetchFile, toBlobURL } from '@ffmpeg/util';
import { BehaviorSubject } from 'rxjs';

// ─── Types ─────────────────────────────────────────────────────────────────

export interface WatermarkOptions {
  logoSrc?:     string;
  logoScale?:   number;
  logoOpacity?: number;
  position?:    'center' | 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right';
  crf?:         number;
  padding?:     number;
}

export interface WatermarkResult {
  blob:    Blob;
  blobUrl: string;
}

// ─── Overlay position expressions ──────────────────────────────────────────

const OVERLAY_POSITIONS: Record<string, string> = {
  'center':       '(main_w-overlay_w)/2:(main_h-overlay_h)/2',
  'top-left':     'PAD:PAD',
  'top-right':    'main_w-overlay_w-PAD:PAD',
  'bottom-left':  'PAD:main_h-overlay_h-PAD',
  'bottom-right': 'main_w-overlay_w-PAD:main_h-overlay_h-PAD',
};

// ─── Service ───────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class VideoWatermarkService {

  readonly progress$   = new BehaviorSubject<number>(0);
  readonly processing$ = new BehaviorSubject<boolean>(false);

  private ffmpeg       = new FFmpeg();
  private ffmpegLoaded = false;

  constructor(private ngZone: NgZone) {}

  // ─── Public API ────────────────────────────────────────────────────────

  /**
   * Adds a watermark logo to a video file using FFmpeg WASM.
   *
   * @example
   *   const { blob, blobUrl } = await this.watermarkService.addWatermark(file, {
   *     logoSrc: 'assets/logo.png', position: 'center', logoOpacity: 0.85
   *   });
   *   // always revoke when done:
   *   URL.revokeObjectURL(blobUrl);
   */
  async addWatermark(file: File, options: WatermarkOptions = {}): Promise<WatermarkResult> {
    const opts = this.resolveOptions(options);
    this.emit(true, 1);
    let timer: ReturnType<typeof setInterval> | null = null;

    try {
      // 1. Load FFmpeg WASM — singleton, cached after first call
      await this.ensureLoaded();
      this.setProgress(8);

      // 2. Start smooth animated progress (FFmpeg worker events are unreliable)
      timer = this.animateProgress(8);

      // 3. Write video into FFmpeg virtual FS
      await this.ffmpeg.writeFile('in.mp4', await fetchFile(file));

      // 4. Fetch logo as raw bytes via browser fetch — most reliable method
      const logoBytes = await this.fetchLogoBytes(opts.logoSrc);

      // 5. Run FFmpeg with or without watermark
      if (logoBytes) {
        await this.ffmpeg.writeFile('logo.png', logoBytes);
        await this.ffmpeg.exec(this.buildArgs(opts, true));
      } else {
        console.warn('[VideoWatermark] Logo unavailable — encoding without watermark');
        await this.ffmpeg.exec(this.buildArgs(opts, false));
      }

      // 6. Snap to 100%, brief pause so user sees completion
      clearInterval(timer!);
      timer = null;
      this.setProgress(100);
      await this.delay(500);

      // 7. Read output
      const data    = await this.ffmpeg.readFile('out.mp4') as Uint8Array;
      const blob    = new Blob([data], { type: 'video/mp4' });
      const blobUrl = URL.createObjectURL(blob);

      // 8. Cleanup virtual FS
      await this.cleanupFS(!!logoBytes);

      return { blob, blobUrl };

    } finally {
      if (timer) clearInterval(timer);
      this.emit(false, 0);
    }
  }

  // ─── Private: FFmpeg loading ──────────────────────────────────────────

  private async ensureLoaded(): Promise<void> {
    if (this.ffmpegLoaded) return;
    const base = 'https://unpkg.com/@ffmpeg/core@0.12.6/dist/umd';
    await this.ffmpeg.load({
      coreURL: await toBlobURL(`${base}/ffmpeg-core.js`,   'text/javascript'),
      wasmURL: await toBlobURL(`${base}/ffmpeg-core.wasm`, 'application/wasm'),
    });
    this.ffmpegLoaded = true;
  }

  // ─── Private: Logo fetching ───────────────────────────────────────────

  private async fetchLogoBytes(src: string): Promise<Uint8Array | null> {
    try {
      const res = await fetch(src, { cache: 'force-cache' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const bytes = new Uint8Array(await res.arrayBuffer());
      console.log(`[VideoWatermark] Logo loaded — ${bytes.byteLength} bytes`);
      return bytes;
    } catch (err) {
      console.warn('[VideoWatermark] Logo fetch failed:', err);
      return null;
    }
  }

  // ─── Private: FFmpeg command builder ─────────────────────────────────

  private buildArgs(opts: Required<WatermarkOptions>, withLogo: boolean): string[] {
    const baseArgs = [
      '-c:v',      'libx264',
      '-preset',   'ultrafast',    // fastest WASM encode
      '-crf',      String(opts.crf),
      '-c:a',      'copy',
      '-movflags', '+faststart',   // web-optimised MP4
      '-y',        'out.mp4',
    ];

    if (!withLogo) {
      return ['-i', 'in.mp4', ...baseArgs];
    }

    const posExpr = (OVERLAY_POSITIONS[opts.position] ?? OVERLAY_POSITIONS['center'])
      .replace(/PAD/g, String(opts.padding));

    // Pipeline: scale → RGBA → opacity → overlay
    const logoChain = [
      `scale=iw*${opts.logoScale}:-1`,
      `format=rgba`,
      `colorchannelmixer=aa=${opts.logoOpacity}`,
    ].join(',');

    const filter = `[1:v]${logoChain}[wm];[0:v][wm]overlay=${posExpr}:format=auto[out]`;

    return [
      '-i',              'in.mp4',
      '-i',              'logo.png',
      '-filter_complex', filter,
      '-map',            '[out]',
      '-map',            '0:a?',    // keep audio if present
      ...baseArgs,
    ];
  }

  // ─── Private: Progress animation ─────────────────────────────────────

  private animateProgress(startFrom: number): ReturnType<typeof setInterval> {
    const t0       = Date.now();
    const TOTAL_MS = 35_000; // 35s conservative ceiling

    return setInterval(() => {
      const raw   = Math.min((Date.now() - t0) / TOTAL_MS, 1);
      const eased = 1 - Math.pow(1 - raw, 2.5);   // quadratic ease-out
      const next  = Math.min(Math.round(startFrom + eased * (92 - startFrom)), 92);
      if (next > this.progress$.value) this.setProgress(next);
    }, 250);
  }

  // ─── Private: Utilities ───────────────────────────────────────────────

  private resolveOptions(o: WatermarkOptions): Required<WatermarkOptions> {
    return {
      logoSrc:     o.logoSrc     ?? 'assets/logo_watermark.png',
      logoScale:   o.logoScale   ?? 0.30,
      logoOpacity: o.logoOpacity ?? 0.85,
      position:    o.position    ?? 'center',
      crf:         o.crf         ?? 26,
      padding:     o.padding     ?? 20,
    };
  }

  private async cleanupFS(hasLogo: boolean): Promise<void> {
    const files = ['in.mp4', 'out.mp4', ...(hasLogo ? ['logo.png'] : [])];
    await Promise.allSettled(files.map(f => this.ffmpeg.deleteFile(f)));
  }

  private emit(processing: boolean, progress: number): void {
    this.ngZone.run(() => {
      this.processing$.next(processing);
      this.progress$.next(progress);
    });
  }

  private setProgress(v: number): void {
    this.ngZone.run(() => this.progress$.next(v));
  }

  private delay(ms: number): Promise<void> {
    return new Promise(r => setTimeout(r, ms));
  }
}